import type { BlockVolume } from '@xver/apis/v1/block-storage/model';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Text for a `role="status"` region: the volumes whose status changed between two polls. The first
 * load announces nothing; render the region before any change so screen readers pick it up.
 */
export function useVolumeStatusAnnouncement(volumes: readonly BlockVolume[] | undefined) {
    const { t } = useTranslation('blockStorage');
    const previous = useRef<ReadonlyMap<string, BlockVolume['status']> | null>(null);
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (!volumes) return;
        const seen = previous.current;
        previous.current = new Map(volumes.map((volume) => [volume.id, volume.status]));
        if (!seen) return;

        const changes = volumes
            .filter((volume) => seen.has(volume.id) && seen.get(volume.id) !== volume.status)
            .map((volume) =>
                t('announce.statusChanged', {
                    name: volume.name,
                    status: t(`status.${volume.status}`),
                })
            );
        if (changes.length > 0) setMessage(changes.join(' '));
    }, [volumes, t]);

    return message;
}
